'use client';
import React, { useState, useEffect } from 'react';
import { Shield, Cookie, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'sedfa_cookie_consent';

type ConsentState = {
  accepted: boolean;
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  timestamp: string;
};

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [functional, setFunctional] = useState(true);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (!stored) {
        // Show banner after a short delay for better UX
        const timer = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(timer);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const saveConsent = (consent: ConsentState) => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
      // Set a long-lived cookie so server-side can also read consent status
      document.cookie = `sedfa_cookie_consent=accepted; path=/; max-age=${365 * 24 * 3600}; SameSite=Lax`;
    } catch {
      // ignore storage errors
    }
    setVisible(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      accepted: true,
      necessary: true,
      functional: true,
      analytics: true,
      timestamp: new Date().toISOString(),
    });
  };

  const handleAcceptSelected = () => {
    saveConsent({
      accepted: true,
      necessary: true,
      functional,
      analytics,
      timestamp: new Date().toISOString(),
    });
  };

  const handleRejectAll = () => {
    saveConsent({
      accepted: false,
      necessary: true,
      functional: false,
      analytics: false,
      timestamp: new Date().toISOString(),
    });
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div className="fixed inset-0 bg-black/30 z-[9998] backdrop-blur-[2px]" aria-hidden="true" />

      {/* Banner */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-desc"
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6 animate-in slide-in-from-bottom duration-300"
      >
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl border border-[hsl(var(--border))] overflow-hidden">
          {/* Header */}
          <div className="flex items-start gap-4 px-5 pt-5 pb-4 border-b border-[hsl(var(--border))]">
            <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))] flex items-center justify-center shrink-0">
              <Cookie size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                id="cookie-banner-title"
                className="text-base font-bold text-[hsl(var(--foreground))]"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                SEDFA Cookie Notice
              </h2>
              <p
                id="cookie-banner-desc"
                className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 leading-relaxed"
              >
                The SEDFA Forum Hub uses cookies to keep you securely logged in, remember your preferences, and improve your experience. By continuing, you agree to our use of cookies as described below.
              </p>
            </div>
          </div>

          {/* Cookie categories */}
          <div className="px-5 py-4 space-y-3">
            {/* Necessary */}
            <div className="flex items-start gap-3 p-3 rounded-xl bg-green-50 border border-green-200">
              <Shield size={16} className="text-green-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-green-800">Strictly Necessary</p>
                  <span className="text-[10px] font-semibold text-green-700 bg-green-100 border border-green-300 px-2 py-0.5 rounded-full">Always On</span>
                </div>
                <p className="text-[11px] text-green-700 mt-0.5 leading-relaxed">
                  Required for authentication, session management, and security. These cannot be disabled.
                </p>
              </div>
            </div>

            {/* Expandable categories */}
            {expanded && (
              <>
                {/* Functional */}
                <div className="flex items-start gap-3 p-3 rounded-xl bg-[hsl(var(--muted)/0.5)] border border-[hsl(var(--border))]">
                  <div className="mt-0.5 shrink-0 w-4 h-4 flex items-center justify-center">
                    <span className="text-[hsl(var(--primary))] text-sm">⚙</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-[hsl(var(--foreground))]">Functional</p>
                      <button
                        role="switch"
                        aria-checked={functional}
                        onClick={() => setFunctional(f => !f)}
                        className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.4)] shrink-0 ${functional ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'}`}
                        aria-label="Toggle functional cookies"
                      >
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${functional ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    </div>
                    <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 leading-relaxed">
                      Remembers your theme, accessibility settings, and language preferences across sessions.
                    </p>
                  </div>
                </div>

                {/* Analytics */}
                <div className="flex items-start gap-3 p-3 rounded-xl bg-[hsl(var(--muted)/0.5)] border border-[hsl(var(--border))]">
                  <div className="mt-0.5 shrink-0 w-4 h-4 flex items-center justify-center">
                    <span className="text-[hsl(var(--primary))] text-sm">📊</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-[hsl(var(--foreground))]">Analytics</p>
                      <button
                        role="switch"
                        aria-checked={analytics}
                        onClick={() => setAnalytics(a => !a)}
                        className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.4)] shrink-0 ${analytics ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'}`}
                        aria-label="Toggle analytics cookies"
                      >
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${analytics ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    </div>
                    <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 leading-relaxed">
                      Helps SEDFA understand how delegates use the forum to improve future events. No personal data is shared with third parties.
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Toggle details */}
            <button
              onClick={() => setExpanded(e => !e)}
              className="flex items-center gap-1.5 text-xs text-[hsl(var(--primary))] hover:underline font-medium"
              aria-expanded={expanded}
            >
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              {expanded ? 'Hide cookie details' : 'Manage cookie preferences'}
            </button>
          </div>

          {/* Footer / Actions */}
          <div className="px-5 pb-5 flex flex-col sm:flex-row items-center gap-2.5">
            <button
              onClick={handleAcceptAll}
              className="w-full sm:w-auto flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-[hsl(var(--primary))] text-white rounded-xl font-semibold text-sm hover:opacity-90 active:scale-95 transition-all"
            >
              Accept All Cookies
            </button>
            {expanded && (
              <button
                onClick={handleAcceptSelected}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] rounded-xl font-semibold text-sm hover:bg-[hsl(var(--primary)/0.05)] active:scale-95 transition-all"
              >
                Save My Preferences
              </button>
            )}
            <button
              onClick={handleRejectAll}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] rounded-xl font-medium text-sm hover:bg-[hsl(var(--muted))] active:scale-95 transition-all"
            >
              Necessary Only
            </button>
          </div>

          {/* Legal footer */}
          <div className="px-5 pb-4 flex items-center gap-1.5 text-[10px] text-[hsl(var(--muted-foreground))]">
            <Shield size={10} />
            <span>SEDFA processes data in accordance with POPIA (South Africa) and applicable data protection laws.</span>
            <a
              href="#"
              className="inline-flex items-center gap-0.5 text-[hsl(var(--primary))] hover:underline font-medium ml-1"
              aria-label="View SEDFA Privacy Policy (opens in new tab)"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy <ExternalLink size={9} />
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
